import React, { FC, useState, useRef, useEffect } from 'react';
import Tooltip from '@mui/material/Tooltip';
import { Button, Grid } from '@mui/material';
import { BlobProvider } from '@react-pdf/renderer';
import DialogContent from '@mui/material/DialogContent';
import makeStyles from '@mui/styles/makeStyles';

import ModalFormTitle from '../../components/Tables/TableWithModalEdit/modalFormPieces/ModalFormTitle';
import { DraggableDialog } from '../DraggablePaper/DraggablePaper';
import { DialogActionsWrapper } from '../Tables/TableWithModalEdit/modalFormPieces/ModalFormActions/ModalFormActions';
import { Theme } from '../../types';
import EmailComposeDialog, { type EmailComposePayload } from '../Email/EmailComposeDialog';
import { displayToast } from '../RTToast';
import vouchersApi from '../../api/vouchers/api';

import type { MergeContext } from '../../utils/mergeFields';
import {
    type EmailDocumentKey,
    EMAIL_PERMISSION_BY_DOCUMENT_KEY
} from '../../api/emailTemplates/documentKeys';
import { useCan } from '../../permissions';

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        padding: theme.spacing(1)
    },
    pdfDialog: {
        height: '90vh'
    },
    pdfViewer: {
        width: '100%',
        height: '100%',
        border: 'none',
        zIndex: 10
    },
    relativeHeight100: {
        height: '100%',
        position: 'relative'
    },
    absoluteHeight100: {
        position: 'absolute',
        height: '100%'
    },
    gridGap2: {
        gridGap: theme.spacing(2)
    },
    gridGap1: {
        gridGap: theme.spacing(1)
    },
    padding1_2: {
        padding: theme.spacing(1, 2)
    },
    widthAuto: {
        width: 'auto'
    }
}));

type Props = {
    open: boolean;
    toggleDialog: (nextValue?: any) => void;
    title: string;
    children: React.ReactElement | null;
    pdfName?: string;
    /**
     * Called **after** a send succeeds, so the caller can write the travel-file log.
     *
     * This replaces `onSendEmailsClick`, which every caller supplied and nothing ever
     * invoked — the modal checked whether it existed and then sent by itself, so the log
     * entry each caller thought it was writing was never written. Sending is this
     * component's job; recording what was sent belongs to the screen that knows what the
     * document is.
     */
    onEmailsSent?: (emails: string[], meta: { templateName?: string; subject: string }) => void;
    /** Which document this is, so the compose dialog offers the messages configured for it. */
    documentKey?: EmailDocumentKey;
    /** Context line for the compose dialog's title bar — travel file, passenger, agency. */
    emailContextHeader?: string;
    /** Values for `<Field Name>` substitution when a template is selected. */
    mergeContext?: MergeContext;
    /** Recipients the dialog opens with. */
    defaultEmails?: string[];
    extraChildren?: React.ReactElement | null;
    extraActions?: React.ReactElement | null;
    autoUploadDocumentId?: string;
    onAutoUploadComplete?: (s3Key: string, url: string) => void;
    /** When set, attempts to load stored PDF from S3 before falling back to dynamic render */
    storedPdfDocumentId?: string;
};

const LOADING_DOCUMENT = 'Loading document...';

/** Set to true to enable S3 upload/fetch for PDFs. Set to false to always use dynamic builder. */
const S3_UPLOAD_DOCS = true;

// Handles S3 auto-upload + captures blob URL for display/download.
// Placed inside BlobProvider's render prop so it has direct access to the blob and url.
type BlobHandlerProps = {
    blob: Blob | null;
    url: string | null;
    autoUploadDocumentId?: string;
    onStatusChange: (status: 'idle' | 'uploading' | 'success' | 'error') => void;
    onAutoUploadComplete?: (s3Key: string, url: string) => void;
    onUrlReady: (url: string | null) => void;
};
const BlobHandler: FC<BlobHandlerProps> = ({ blob, url, autoUploadDocumentId, onStatusChange, onAutoUploadComplete, onUrlReady }) => {
    const uploadAttemptedRef = useRef<string | null>(null);

    useEffect(() => {
        onUrlReady(url);
    }, [url]); // eslint-disable-line

    useEffect(() => {
        if (!S3_UPLOAD_DOCS || !blob || !autoUploadDocumentId || uploadAttemptedRef.current === autoUploadDocumentId) {
            return;
        }
        uploadAttemptedRef.current = autoUploadDocumentId;
        onStatusChange('uploading');
        vouchersApi.uploadPdf(autoUploadDocumentId, blob)
            .then((result) => {
                onStatusChange('success');
                if (onAutoUploadComplete && result.s3Key && result.url) {
                    onAutoUploadComplete(result.s3Key, result.url);
                }
            })
            .catch((err) => {
                console.error('Auto-upload failed:', err);
                onStatusChange('error');
            });
    }, [blob, autoUploadDocumentId]); // eslint-disable-line

    return null;
};

const PdfViewerModal: FC<Props> = (props: Props) => {
    const {
        open,
        toggleDialog,
        title,
        children,
        pdfName = 'pdf',
        onEmailsSent,
        documentKey,
        emailContextHeader,
        mergeContext,
        defaultEmails,
        extraChildren,
        extraActions,
        autoUploadDocumentId,
        onAutoUploadComplete,
        storedPdfDocumentId
    } = props;
    const classes = useStyles();

    const [composeOpen, setComposeOpen] = useState(false);

    // Emailing a document is gated on that document type's own `:email`
    // permission. A document type with no email permission in the catalog
    // (reports, Statement, Deposit, Withdrawal) keeps its Send button.
    const can = useCan();
    const emailPermission = documentKey ? EMAIL_PERMISSION_BY_DOCUMENT_KEY[documentKey] : undefined;
    const canEmailDocument = emailPermission ? can(emailPermission) : true;
    const [sending, setSending] = useState(false);
    const [uploadStatus, setUploadStatus] = useState<'idle' | 'uploading' | 'success' | 'error'>('idle');
    const [downloadUrl, setDownloadUrl] = useState<string | null>(null);

    // Try to load stored PDF from S3 before falling back to dynamic render
    const [storedPdfUrl, setStoredPdfUrl] = useState<string | null>(null);
    const [storedPdfLoading, setStoredPdfLoading] = useState(S3_UPLOAD_DOCS && !!storedPdfDocumentId);
    const storedPdfAttemptedRef = useRef<string | null>(null);

    useEffect(() => {
        if (!S3_UPLOAD_DOCS || !storedPdfDocumentId || storedPdfAttemptedRef.current === storedPdfDocumentId) return;
        storedPdfAttemptedRef.current = storedPdfDocumentId;
        setStoredPdfLoading(true);
        vouchersApi.getPdfUrl(storedPdfDocumentId)
            .then((result) => {
                if (result?.url) {
                    console.log('[PdfViewerModal] Loading PDF from S3 URL', { documentId: storedPdfDocumentId });
                    setStoredPdfUrl(result.url);
                    setDownloadUrl(result.url);
                }
            })
            .catch(() => {
                console.log('[PdfViewerModal] No stored PDF, falling back to dynamic render', { documentId: storedPdfDocumentId });
            })
            .finally(() => {
                setStoredPdfLoading(false);
            });
    }, [storedPdfDocumentId]);

    const useStoredPdf = !!storedPdfUrl;

    // Capture the blob from BlobProvider for use in email sending.
    // Updating a ref during render is safe — refs are mutable and don't trigger re-renders.
    const blobRef = useRef<Blob | null>(null);

    const sendComposedEmail = async (payload: EmailComposePayload) => {
        // A document displayed from S3 was never rendered here, so there is no blob —
        // the server attaches the stored copy from `documentId` instead. Only when there
        // is neither is there genuinely nothing to send.
        if (!blobRef.current && !storedPdfDocumentId) {
            displayToast('info', 'PDF not ready', 'Please wait for the PDF to finish loading.');
            return;
        }
        if (sending) return;
        setSending(true);
        try {
            const cleanedName = pdfName.trim().replace(/\s+/g, '-').replace(/[^\w.-]/g, '');
            const fileName = `${cleanedName}.pdf`;

            const formData = new FormData();
            if (blobRef.current) {
                formData.append('pdf', new File([blobRef.current], fileName, { type: 'application/pdf' }));
            } else if (storedPdfDocumentId) {
                formData.append('documentId', String(storedPdfDocumentId));
            }
            formData.append('emails', JSON.stringify(payload.emails));
            formData.append('title', title || 'Document');
            formData.append('subject', payload.subject || title || 'Document');
            formData.append('body', payload.body || '');
            if (payload.language) formData.append('language', payload.language);
            // Decides which of the client's senders the message goes out as — finance
            // for a receipt, the service desk for a confirmation.
            if (documentKey) formData.append('documentKey', documentKey);
            formData.append('fileName', fileName);
            payload.attachments.forEach((file) => formData.append('attachments', file));
            // No clientId — the server derives it from the session. Sending it from
            // the browser would let a caller send as another client.

            const res = await fetch('/send-email-with-pdf', { method: 'POST', body: formData });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.message || `Server error ${res.status}`);
            }

            const result = await res.json().catch(() => ({}));
            if (result?.failedRecipients) {
                displayToast(
                    'warning',
                    'Partly sent',
                    `${result.failedRecipients} of ${payload.emails.length} recipients could not be reached.`
                );
            } else {
                displayToast('success', 'Emails sent!', `${payload.emails.join(', ')}`);
            }

            setComposeOpen(false);
            // Only after the send actually succeeded: a log entry for mail that never
            // left is worse than no entry at all.
            if (onEmailsSent) {
                onEmailsSent(payload.emails, {
                    templateName: payload.templateName,
                    subject: payload.subject
                });
            }
        } catch (error) {
            console.error('Error sending email:', error);
            displayToast('error', 'Error!', (error as Error)?.message || 'Failed to send email with PDF.');
        } finally {
            setSending(false);
        }
    };

    return (
        <DraggableDialog
            classes={{ paper: classes.pdfDialog }}
            open={open}
            onClose={toggleDialog}
            fullWidth
            maxWidth="lg"
            disableBackdropClick
        >
            <ModalFormTitle title={title} toggleDialog={toggleDialog} />
            <DialogContent style={{ height: 'calc(90vh - 140px)', padding: 0, overflow: 'hidden' }}>
                {storedPdfLoading ? (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        {LOADING_DOCUMENT}
                    </div>
                ) : useStoredPdf ? (
                    // Stored PDF from S3 — display directly in iframe
                    <iframe
                        src={storedPdfUrl!}
                        className={classes.pdfViewer}
                        title={title}
                    />
                ) : children && (
                    // If children is an HTML element (error/status message), render it directly.
                    // BlobProvider only accepts react-pdf <Document> components — passing HTML crashes it.
                    typeof (children as any).type === 'string' ? (
                        <div style={{ width: '100%', height: '100%', overflow: 'auto' }}>
                            {children}
                        </div>
                    ) : (
                    // Single BlobProvider handles display (via iframe) + auto-upload + email blob.
                    // This avoids the concurrent render crash that occurred when PDFViewer and
                    // pdf().toBlob() both rendered the same document simultaneously in react-pdf v3.
                    <BlobProvider document={children}>
                        {({ blob, url, loading, error }) => {
                            // Capture blob for email sending (safe to update ref during render)
                            blobRef.current = blob;
                            if (error) console.error('BlobProvider render error:', error);

                            return (
                                <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                                    <BlobHandler
                                        blob={blob}
                                        url={url}
                                        autoUploadDocumentId={autoUploadDocumentId}
                                        onStatusChange={setUploadStatus}
                                        onAutoUploadComplete={onAutoUploadComplete}
                                        onUrlReady={setDownloadUrl}
                                    />
                                    {uploadStatus === 'uploading' && (
                                        <div style={{
                                            position: 'absolute',
                                            top: 5,
                                            left: 5,
                                            zIndex: 100,
                                            background: '#ff9800',
                                            color: 'white',
                                            padding: '4px 8px',
                                            borderRadius: 4,
                                            fontSize: 12
                                        }}>
                                            Saving...
                                        </div>
                                    )}
                                    {uploadStatus === 'success' && (
                                        <div style={{
                                            position: 'absolute',
                                            top: 5,
                                            left: 5,
                                            zIndex: 100,
                                            background: '#4caf50',
                                            color: 'white',
                                            padding: '4px 8px',
                                            borderRadius: 4,
                                            fontSize: 12
                                        }}>
                                            Saved ✓
                                        </div>
                                    )}
                                    {!loading && url && (
                                        <iframe
                                            src={url}
                                            className={classes.pdfViewer}
                                            title={title}
                                        />
                                    )}
                                </div>
                            );
                        }}
                    </BlobProvider>
                    )
                )}
            </DialogContent>
            <DialogActionsWrapper>
                <Grid container>
                    <Grid
                        className={`${classes.padding1_2} ${classes.gridGap2}`}
                        container
                        alignItems="center"
                        justifyContent="space-between"
                        wrap="nowrap"
                    >
                        <Grid className={`${classes.gridGap1} ${classes.widthAuto}`} container wrap="nowrap" alignItems="center">
                            <Grid className={classes.widthAuto} container alignItems="center">
                                {/* Opens the compose dialog rather than sending straight
                                    away: recipients, subject, message and attachments are
                                    all decided there. */}
                                <Tooltip
                                    title={
                                        canEmailDocument
                                            ? ''
                                            : 'You do not have permission to email this document'
                                    }
                                >
                                    {/* A disabled button swallows pointer events, so the
                                        tooltip needs a plain wrapper to hang off. */}
                                    <span>
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            disabled={sending || !canEmailDocument}
                                            onClick={() => setComposeOpen(true)}
                                        >
                                            {sending ? 'Sending...' : 'Send'}
                                        </Button>
                                    </span>
                                </Tooltip>
                            </Grid>
                        </Grid>
                        <Grid style={{ alignSelf: 'center' }}>
                            {children && downloadUrl ? (
                                <>
                                    <a
                                        href={downloadUrl}
                                        download={`${pdfName}.pdf`}
                                        style={{ textDecoration: 'none' }}
                                    >
                                        <Button variant="contained" color="secondary">
                                            Download PDF
                                        </Button>
                                    </a>
                                    {!!extraActions && extraActions}
                                </>
                            ) : (
                                <Button variant="outlined" color="secondary" disabled>
                                    {LOADING_DOCUMENT}
                                </Button>
                            )}
                        </Grid>
                    </Grid>
                    {extraChildren ? extraChildren : <></>}
                </Grid>
            </DialogActionsWrapper>

            <EmailComposeDialog
                open={composeOpen}
                onClose={() => setComposeOpen(false)}
                contextHeader={emailContextHeader || title}
                documentKey={documentKey}
                mergeContext={mergeContext}
                defaultSubject={title}
                defaultEmails={defaultEmails}
                documentSizeBytes={blobRef.current?.size || 0}
                sending={sending}
                onSend={sendComposedEmail}
            />
        </DraggableDialog>
    );
};

export default PdfViewerModal;
